import { useForm } from 'formoid';
import { FormEvent } from 'react';
import { CheckboxField, InputField, PasswordField, Button, Link } from '~/common/components';
import { customValidator, propagateBackendValidationErrors } from '~/common/utils';
import { OauthProviders } from '~/components/OauthProviders';
import { routes } from '~/constants';
import { useLogin } from '~/root/Auth';

const initialValues = {
  email: '',
  password: '',
  remember: false,
};

export const LoginForm = ({
  onClose,
  onSuccess,
  className,
  allowGuestCheckout = false,
}: {
  onClose?: () => void;
  onSuccess?: () => void;
  className?: string;
  allowGuestCheckout?: boolean;
}) => {
  const login = useLogin();

  const { fieldProps, handleSubmit, setErrors, errors } = useForm({
    initialValues,
    validationStrategy: 'onBlur',
    validators: () => ({
      password: null,
      email: customValidator.email(),
      remember: null,
    }),
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit((values) => {
      return login
        .mutateAsync(values)
        .then(onSuccess)
        .then(onClose)
        .catch((error: unknown) => {
          propagateBackendValidationErrors({ error, setErrors, formErrors: errors });
        });
    });
  };

  return (
    <div className={className}>
      <h2 className="font-brand-t2b mb-1">Log in</h2>
      {allowGuestCheckout && (
        <p className="font-brand-b3 mb-1 text-text-400">
          Log in now to skip any surprises later - we'll confirm your account is set up for this
          order flow right away.
        </p>
      )}
      <p className="font-brand-b3 mb-3 text-text-400">
        Don’t have an account?&nbsp;
        <Link to={routes.signup}>Sign up</Link>
      </p>

      <OauthProviders className="mb-3" />

      <form className="space-y-3" onSubmit={submit}>
        <div className="space-y-2">
          <InputField
            {...fieldProps('email')}
            title="Email"
            type="email"
            placeholder="Enter your email"
          />

          <PasswordField
            {...fieldProps('password')}
            title="Password"
            placeholder="Enter your password"
          />
        </div>

        <Link to={routes.reset_password}>Forgot password?</Link>

        <CheckboxField {...fieldProps('remember')} title="Remember me" />

        <Button
          className="w-full !mt-4"
          color="secondary-navy"
          size="m"
          loading={login.isLoading}
          type="submit"
        >
          {allowGuestCheckout ? 'Log in' : 'Continue'}
        </Button>
      </form>

      {allowGuestCheckout && onClose && (
        <div className="flex flex-col gap-[12px] items-center mt-2">
          <p className="font-brand-b3 text-text-400">
            In a hurry? No problem, you can register later.
          </p>
          <Button className="w-full" color="outlined-navy" size="m" onClick={onClose}>
            Continue as a guest
          </Button>
        </div>
      )}
    </div>
  );
};
